import { FC } from 'react';
import { useIntl } from 'dibs-react-intl';
import { createFragmentContainer, graphql } from 'react-relay';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel';
import popularSearchesSitemapMessages from '../helpers/popularSearchesSitemapMessages';
import { Breadcrumbs, BreadcrumbsProps } from 'dibs-elements/exports/Breadcrumbs';
import {
    PopularSearchesSitemapLinksBlock,
    MOST_POPULAR_BUY_PAGES,
    DESCENDANT_CATEGORIES,
    OTHER_POPULAR_BUY_PAGES,
} from './PopularSearchesSitemapLinksBlock';
import { PageLinks } from 'dibs-page-links/exports/PageLinks';
import { POPULAR_SEARCHES_ROUTE, QP_PAGE } from '../../constants/popularSearchesSitemapConstants';
import classNames from 'classnames';
import styles from './PopularSearchesSitemapLayout.scss';
import type { PopularSearchesSitemapLayout_viewer } from './__generated__/PopularSearchesSitemapLayout_viewer.graphql';

type Props = {
    viewer: PopularSearchesSitemapLayout_viewer;
    relayVariables: {
        uriRef: string;
        page: number;
    };
};

type PopularSearchesSitemapType = NonNullable<
    PopularSearchesSitemapLayout_viewer['popularSearchesSitemap']
>;

const PopularSearchesSitemapLayoutComponent: FC<Props> = ({ viewer, relayVariables }) => {
    const { formatMessage } = useIntl();
    const { uriRef, page } = relayVariables;
    const { popularSearchesSitemap } = viewer;
    const {
        breadcrumbs,
        categoryName,
        mostPopularBuyPages,
        descendantCategories,
        otherPopularBuyPages,
    } = popularSearchesSitemap as PopularSearchesSitemapType;

    const {
        mostPopularBuyPagesTitle,
        descendantCategoriesTitle,
        otherPopularBuyPagesTitle,
    } = popularSearchesSitemapMessages;

    const mostPopularBuyPagesTitleMsg = formatMessage(mostPopularBuyPagesTitle, { categoryName });
    const descendantCategoriesTitleMsg = formatMessage(descendantCategoriesTitle, { categoryName });
    const otherPopularBuyPagesTitleMsg = formatMessage(otherPopularBuyPagesTitle);

    const numberOfPages = otherPopularBuyPages?.numberOfPages || 1;
    const hasDescendantCategories = !!descendantCategories?.length;

    const getUrlForPage = (pageNumber: number): string =>
        `${POPULAR_SEARCHES_ROUTE}${uriRef}${pageNumber === 1 ? '' : `?${QP_PAGE}=${pageNumber}`}`;

    const handlePageChange = ({ pageNumber }: { pageNumber: number }): void => {
        window.location.href = getUrlForPage(pageNumber);
    };

    return (
        <HeadingLevel>
            {Heading => (
                <>
                    <div className={styles.breadcrumbs}>
                        <Breadcrumbs breadcrumbs={breadcrumbs as BreadcrumbsProps['breadcrumbs']} />
                    </div>

                    {/* Popular Searches in {categoryName} */}
                    {hasDescendantCategories && (
                        <PopularSearchesSitemapLinksBlock
                            dataTn={MOST_POPULAR_BUY_PAGES}
                            headerElement={Heading}
                            title={mostPopularBuyPagesTitleMsg}
                            links={null}
                            connectionLinks={mostPopularBuyPages}
                        />
                    )}
                    {/* All {categoryName} Searches by Category */}
                    <PopularSearchesSitemapLinksBlock
                        dataTn={DESCENDANT_CATEGORIES}
                        headerElement={Heading}
                        title={descendantCategoriesTitleMsg}
                        links={descendantCategories}
                        connectionLinks={null}
                    />
                    {/* Other Popular Searches */}
                    <PopularSearchesSitemapLinksBlock
                        dataTn={OTHER_POPULAR_BUY_PAGES}
                        headerElement={Heading}
                        title={
                            hasDescendantCategories
                                ? otherPopularBuyPagesTitleMsg
                                : mostPopularBuyPagesTitleMsg
                        }
                        links={null}
                        connectionLinks={otherPopularBuyPages}
                    />
                    {/* Other Popular Searches Pagination */}
                    <div className={classNames({ [styles.pageLinks]: numberOfPages > 1 })}>
                        <PageLinks
                            numberOfPages={numberOfPages}
                            currentPage={page}
                            onPageChange={handlePageChange}
                            getPageLinkUrl={getUrlForPage}
                        />
                    </div>
                </>
            )}
        </HeadingLevel>
    );
};

export const PopularSearchesSitemapLayout = createFragmentContainer(
    PopularSearchesSitemapLayoutComponent,
    {
        viewer: graphql`
            fragment PopularSearchesSitemapLayout_viewer on Viewer {
                popularSearchesSitemap(uriRef: $uriRef) {
                    categoryName
                    breadcrumbs {
                        displayName: text
                        path
                    }
                    mostPopularBuyPages: popularBuyPages(first: 40) {
                        ...PopularSearchesSitemapLinksBlock_connectionLinks
                    }
                    descendantCategories {
                        ...PopularSearchesSitemapLinksBlock_links
                    }
                    otherPopularBuyPages: popularBuyPages(
                        first: 200
                        pageStartOffset: 40
                        page: $page
                    ) {
                        numberOfPages
                        ...PopularSearchesSitemapLinksBlock_connectionLinks
                    }
                }
            }
        `,
    }
);
